import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import {
  Search, Edit, Trash2, ChevronLeft, ChevronRight, FolderKanban, ArrowRightLeft, FilterX,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Brand { id: string; name: string; code: string; primaryColor?: string; }

interface ExpedientComercial {
  id: string;
  ecNumber: string;
  name: string;
  etiquetaExpedient?: string | null;
  estat: string;
  responsableEmpresa?: string | null;
  compositeCodeId?: number | null;
  compositeSubfamilyId?: number | null;
  client?: { id: string; name: string };
  brand?: { id: string; name: string; code: string; primaryColor?: string };
  project?: { id: string; projectNumber: string } | null;
  budgetsCount?: number;
  createdAt: string;
}

// Mateixos colors que les etiquetes d'estat de Licitacions (Licitacions.tsx: STATUS_CONFIG)
// pels 3 estats comuns: Licitat=blau (primary), Desestimat=neutre (outline), Proposat=verd (success).
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  LICITAT:    { label: 'Licitat',    className: 'bg-blue-100 text-blue-700' },
  DESESTIMAT: { label: 'Desestimat', className: 'bg-slate-100 text-slate-700' },
  ADJUDICAT:  { label: 'Proposat',   className: 'bg-green-100 text-green-700' },
  FACTIBLE:   { label: 'Factible',  className: 'bg-amber-100 text-amber-700' },
  DESCARTAT:  { label: 'Descartat', className: 'bg-gray-100 text-gray-600' },
  ACCEPTAT:   { label: 'Comanda',   className: 'bg-emerald-100 text-emerald-700' },
  LLIURAT:    { label: 'Lliurat',   className: 'bg-purple-100 text-purple-700' },
  TANCAT:     { label: 'Facturat',  className: 'bg-slate-200 text-slate-700' },
};

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('ca-ES') : '—');

export default function ExpedientComercialList() {
  const navigate = useNavigate();

  const [items, setItems]       = useState<ExpedientComercial[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]             = useSessionState('ec:search', '');
  const [statusFilter, setStatusFilter] = useSessionState('ec:statusFilter', '');
  const [brandFilter, setBrandFilter]   = useSessionState('ec:brandFilter', '');
  const [responsableFilter, setResponsableFilter] = useSessionState('ec:responsableFilter', '');
  const [compositeSubfamilyFilter, setCompositeSubfamilyFilter] = useSessionState('ec:compositeSubfamilyFilter', '');
  const [compositeProductFilter, setCompositeProductFilter] = useSessionState('ec:compositeProductFilter', '');
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<{ id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean }[]>([]);
  const [brands, setBrands]     = useState<Brand[]>([]);
  const [companyUsers, setCompanyUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);
  const [statusMenuRect, setStatusMenuRect] = useState<DOMRect | null>(null);
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const [estatsOpts, setEstatsOpts] = useState<{ code: string; name: string }[]>([]);
  const [sortBy, setSortBy]   = useSessionState<string>('ec:sortBy', 'createdAt');
  const [sortDir, setSortDir] = useSessionState<'ASC' | 'DESC'>('ec:sortDir', 'DESC');

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortDir('ASC'); }
    setPage(1);
  };
  const sortIcon = (col: string) => sortBy === col
    ? <span className="ml-1 text-foreground">{sortDir === 'ASC' ? '↑' : '↓'}</span>
    : <span className="ml-1 text-muted-foreground/40">↕</span>;

  useEffect(() => {
    api.get<{ success: boolean; data: any[] }>('/admin/estats-crm', { module: 'ec' })
      .then((res) => setEstatsOpts((res.data ?? []).filter((e: any) => e.isActive)))
      .catch(() => setEstatsOpts([]));
    api.get<{ success: boolean; data: Brand[] }>('/brands').then((r) => setBrands(r.data ?? [])).catch(() => {});
    api.get<{ success: boolean; data: { id: string; firstName: string; lastName: string }[] }>('/auth/users')
      .then((r) => setCompanyUsers(r.data ?? [])).catch(() => setCompanyUsers([]));
    api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies')
      .then((r) => setCompositeSubfamilies((r.data ?? []).filter((s) => Number(s.isActive) === 1))).catch(() => {});
    api.get<{ success: boolean; data: typeof compositeProducts }>('/catalog/composite-products')
      .then((r) => setCompositeProducts((r.data ?? []).filter((p) => Number(p.isActive) === 1))).catch(() => {});
  }, []);

  // Etiqueta real del catàleg admin (Administració › Estats Expedient Comercial);
  // el codi de color de STATUS_CONFIG es manté fix per codi de negoci.
  const estatLabel = (code: string) => estatsOpts.find((e) => e.code === code)?.name ?? STATUS_CONFIG[code]?.label ?? code;
  const otherEstats = (current: string) => estatsOpts.map((e) => e.code).filter((code) => code !== current);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: 20, sortBy, sortDir };
      if (search) params.search = search;
      if (statusFilter) params.estat = statusFilter;
      if (brandFilter) params.brandId = brandFilter;
      if (responsableFilter) params.responsableEmpresa = responsableFilter;
      if (compositeSubfamilyFilter) params.compositeSubfamilyId = compositeSubfamilyFilter;
      if (compositeProductFilter) params.compositeProductId = compositeProductFilter;

      const res = await api.get<any>('/expedients-comercials', params);
      setItems(res.data ?? []);
      setTotalPages(res.pagination?.totalPages ?? 1);
    } catch (err) {
      console.error('Error carregant expedients comercials:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, brandFilter, responsableFilter, compositeSubfamilyFilter, compositeProductFilter, sortBy, sortDir]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleChangeStatus = async (id: string, newEstat: string) => {
    setStatusMenuId(null);
    setChangingStatus(id);
    try {
      await api.put(`/expedients-comercials/${id}`, { estat: newEstat });
      fetchItems();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Error canviant l\'estat');
    } finally {
      setChangingStatus(null);
    }
  };

  const handleDelete = async (id: string, num: string) => {
    if (!confirm(`Eliminar l'expedient comercial ${num}?`)) return;
    setDeleting(id);
    try {
      await api.delete(`/expedients-comercials/${id}`);
      fetchItems();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Error eliminant');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); fetchItems(); }}
            className="flex flex-wrap gap-3"
          >
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter(''); setBrandFilter(''); setResponsableFilter(''); setPage(1); }}
              title="Netejar tots els filtres"
              className="flex items-center justify-center px-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shrink-0"
            >
              <FilterX size={16} className="text-muted-foreground" />
            </button>
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cercar per número, nom, client..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {brands.length > 0 && (
              <ExcelFilterDropdown
                label="Marca"
                options={brands.map((b) => ({ value: b.id, label: b.name }))}
                selected={brandFilter ? brandFilter.split(',') : []}
                onApply={(values) => { setBrandFilter(values.join(',')); setPage(1); }}
              />
            )}
            <ExcelFilterDropdown
              label="Estat"
              options={estatsOpts.map((e) => ({ value: e.code, label: e.name }))}
              selected={statusFilter ? statusFilter.split(',') : []}
              onApply={(values) => { setStatusFilter(values.join(',')); setPage(1); }}
            />
            {companyUsers.length > 0 && (
              <ExcelFilterDropdown
                label="Responsable"
                options={companyUsers.map((u) => ({ value: `${u.firstName} ${u.lastName}`, label: `${u.firstName} ${u.lastName}` }))}
                selected={responsableFilter ? responsableFilter.split(',') : []}
                onApply={(values) => { setResponsableFilter(values.join(',')); setPage(1); }}
              />
            )}
            <ExcelFilterDropdown
              label="Subfamília"
              options={compositeSubfamilies.map((s) => ({ value: String(s.id), label: s.name }))}
              selected={compositeSubfamilyFilter ? compositeSubfamilyFilter.split(',') : []}
              onApply={(values) => { setCompositeSubfamilyFilter(values.join(',')); setPage(1); }}
            />
            <ExcelFilterDropdown
              label="Producte"
              options={compositeProducts
                .filter((p) => !compositeSubfamilyFilter || compositeSubfamilyFilter.split(',').includes(String(p.subfamilyId)))
                .map((p) => ({ value: String(p.id), label: p.name }))}
              selected={compositeProductFilter ? compositeProductFilter.split(',') : []}
              onApply={(values) => { setCompositeProductFilter(values.join(',')); setPage(1); }}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-xs hover:bg-gray-700 transition-colors"
            >
              Cercar
            </button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FolderKanban size={40} className="mb-3 opacity-30" />
              <p className="text-xs">No s'han trobat expedients comercials</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('createdAt')}>Data{sortIcon('createdAt')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('ecNumber')}>Número{sortIcon('ecNumber')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('brand')}>Marca{sortIcon('brand')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('name')}>Nom{sortIcon('name')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('client')}>Client{sortIcon('client')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('responsableEmpresa')}>Responsable{sortIcon('responsableEmpresa')}</TableHead>
                  <TableHead>Subfamília</TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('estat')}>Estat{sortIcon('estat')}</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((ec) => {
                  const cfg = { label: estatLabel(ec.estat), className: STATUS_CONFIG[ec.estat]?.className ?? 'bg-gray-100 text-gray-600' };
                  return (
                    <TableRow
                      key={ec.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/crm/expedients-comercials/${ec.id}/edit`)}
                    >
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(ec.createdAt)}</TableCell>
                      <TableCell className="font-medium">{ec.ecNumber}</TableCell>
                      <TableCell>
                        {ec.brand && (
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: ec.brand.primaryColor || '#6b7280' }}
                          >
                            {ec.brand.code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate">{ec.etiquetaExpedient || ec.name}</TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate">{ec.client?.name ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{ec.responsableEmpresa ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{compositeSubfamilies.find((s) => s.id === ec.compositeSubfamilyId)?.name ?? '—'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
                          {cfg.label}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {otherEstats(ec.estat).length > 0 && (
                            <div className="relative">
                              <button
                                title="Canviar estat"
                                onClick={(e) => {
                                  if (statusMenuId === ec.id) {
                                    setStatusMenuId(null);
                                    setStatusMenuRect(null);
                                  } else {
                                    setStatusMenuRect(e.currentTarget.getBoundingClientRect());
                                    setStatusMenuId(ec.id);
                                  }
                                }}
                                disabled={changingStatus === ec.id}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40"
                              >
                                <ArrowRightLeft size={14} />
                              </button>
                              {statusMenuId === ec.id && statusMenuRect && createPortal(
                                <>
                                  <div
                                    className="fixed inset-0 z-[9998]"
                                    onClick={() => { setStatusMenuId(null); setStatusMenuRect(null); }}
                                  />
                                  <div
                                    style={{
                                      position: 'fixed',
                                      right: window.innerWidth - statusMenuRect.right,
                                      ...(statusMenuRect.bottom + 8 + otherEstats(ec.estat).length * 30 > window.innerHeight
                                        ? { bottom: window.innerHeight - statusMenuRect.top + 4 }
                                        : { top: statusMenuRect.bottom + 4 }),
                                      zIndex: 9999,
                                    }}
                                    className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
                                  >
                                    {otherEstats(ec.estat).map((estat) => {
                                      const dotClass = STATUS_CONFIG[estat]?.className.split(' ')[0] ?? 'bg-gray-300';
                                      return (
                                        <button
                                          key={estat}
                                          onClick={() => { handleChangeStatus(ec.id, estat); setStatusMenuRect(null); }}
                                          className="flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors"
                                        >
                                          <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
                                          {estatLabel(estat)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>,
                                document.body,
                              )}
                            </div>
                          )}
                          <button
                            title="Editar"
                            onClick={() => navigate(`/crm/expedients-comercials/${ec.id}/edit`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => handleDelete(ec.id, ec.ecNumber)}
                            disabled={deleting === ec.id || !['PRESENTAT_PENDENT', 'DESESTIMAT', 'ADJUDICAT', 'FACTIBLE', 'PRESENTAT_PERDUT'].includes(ec.estat)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-40"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Pàgina {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
